import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Plus,
  Search,
  Filter,
  ChevronDown,
  AlertCircle,
  Download,
} from "lucide-react";
import { customersAPI } from "../../services";
import { toast } from "react-toastify";
import FinanceTransactionForm from "./FinanceTransactionForm";
import EditFinanceTransactionForm from "./EditFinanceTransactionForm";
import CustomerAnalytics from "./CustomerAnalytics";

import * as XLSX from "xlsx";
import "../../style/customerDetails.css";
import "../../style/customers.css";
import MaskedCurrencyValue from "../common/MaskedCurrencyValue";

// Import export icons
import FilePdf from "../../assets/file-pdf.svg";
import FileExcel from "../../assets/microsoft-excel-logo.svg";
import FileCsv from "../../assets/file-csv.svg";
import DeleteIcon from "../../assets/delete-modal-icon.svg";

import SaleIcon from "../../assets/icons/SaleIcon";
import ReturnIcon from "../../assets/icons/ReturnIcon";
import CreditIcon from "../../assets/icons/CreditIcon";
import DebitIcon from "../../assets/icons/DebitIcon";
import EditIcon from "../../assets/icons/EditIcon";
import EyeIcon from "../../assets/icons/EyeIcon";
import RedDeleteIcon from "../../assets/icons/RedDeleteIcon";
import { useSelector } from "react-redux";

const CustomerDetails = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { isPartsURL, isAccessoriesURL, isGodownURL } = useSelector((state) => state.domain);
  const themeSuffix = isPartsURL
    ? "-parts"
    : isGodownURL
    ? "-godown"
    : isAccessoriesURL
    ? "-acc"
    : "";
  const themeClass = (base) => `${base}${themeSuffix}`;
  const themePrimaryVar = isPartsURL
    ? "var(--primary-color-parts)"
    : isGodownURL
    ? "var(--primary-color-godown)"
    : isAccessoriesURL
    ? "var(--primary-color-acc)"
    : "var(--primary-color-mobile)";
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summaryStats, setSummaryStats] = useState({
    current_balance: 0,
    total_orders: 0,
    returns: 0,
  });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showFinanceForm, setShowFinanceForm] = useState(false);
  const [showEditFinanceForm, setShowEditFinanceForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [deletingTransaction, setDeletingTransaction] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const exportDropdownRef = useRef(null);
  const filterDropdownRef = useRef(null);
  const [balanceFilter, setBalanceFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const datePickerRef = useRef(null);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const fetchedIdRef = useRef(null);
  const isFetchingRef = useRef(false);
  
  // Get the return URL from search params and decode it to restore query parameters
  const returnToUrl = searchParams.get("returnTo") 
    ? decodeURIComponent(searchParams.get("returnTo")) 
    : "/customers";

  // Fetch customer data from API
  const fetchCustomerDetail = async () => {
    setLoading(true);
    try {
      const result = await customersAPI.getCustomerDetail(id);
      if (result.error) {
        toast.error(result.error.message || "Failed to fetch customer details");
        setCustomer(null);
        return;
      }

      const customerData = result.response?.data || result.response;
      setCustomer(customerData);
    } catch (error) {
      console.error("Error fetching customer detail:", error);
      toast.error("Failed to fetch customer details");
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch customer summary statistics
  const fetchCustomerSummaryStats = async () => {
    setSummaryLoading(true);
    try {
      const result = await customersAPI.getCustomerDetailsCount(id);
      if (result.error) {
        console.error(
          "Failed to fetch customer summary stats:",
          result.error.message
        );
        toast.error(
          result.error.message || "Failed to fetch customer statistics"
        );
        return;
      }

      const stats = result.response?.data || result.response || {};

      const newSummaryStats = {
        current_balance: stats.balance || 0,
        total_orders: stats.total_orders || 0,
        returns: stats.total_returns || 0,
      };

      setSummaryStats(newSummaryStats);
    } catch (error) {
      console.error("Error fetching customer summary stats:", error);
      toast.error("Failed to fetch customer statistics");
    } finally {
      setSummaryLoading(false);
    }
  };

  // Fetch transactions from Customer API
  const fetchTransactions = async (searchQuery = "", filters = {}) => {
    try {
      // Helper to format date as YYYY-MM-DD in local timezone
      const formatLocalDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      // Prepare filters object with current state values
      const filterParams = {
        ...filters,
        start_date: selectedStartDate
          ? formatLocalDate(selectedStartDate)
          : null,
        end_date: selectedEndDate ? formatLocalDate(selectedEndDate) : null,
        type: typeFilter !== "all" ? typeFilter : null,
        balance:
          balanceFilter !== "all"
            ? balanceFilter === "positive"
              ? "credit"
              : "debit"
            : null,
      };

      // Remove null/undefined values
      const cleanFilters = Object.fromEntries(
        Object.entries(filterParams).filter(
          ([_, value]) => value !== null && value !== undefined
        )
      );

      const result = await customersAPI.getCustomerFinanceJournals(
        id,
        searchQuery,
        cleanFilters
      );

      if (result.error) {
        console.error(
          "Failed to fetch customer finance journals:",
          result.error.message
        );
        toast.error(result.error.message || "Failed to fetch transactions");
        setTransactions([]);
        return;
      }

      // Handle different response structures
      let journalData = [];
      if (result.response?.data) {
        journalData = Array.isArray(result.response.data)
          ? result.response.data
          : [];
      } else if (Array.isArray(result.response)) {
        journalData = result.response;
      }

      if (journalData.length === 0) {
        setTransactions([]);
        return;
      }

      // Transform journal data to transaction format
      const transformedTransactions = journalData.map((journal) => {
        // Extract sales ID from note if sales field is null
        let salesId = journal.sales;
        if (!salesId && journal.note) {
          const match = journal.note.match(/Sales id : (\d+)/);
          if (match) salesId = parseInt(match[1]);
        }

        return {
          id: journal.id,
          customerId: id,
          date: journal.tnx_date,
          invoiceId: journal.tnx_id,
          type: journal.return_id
            ? "Return"
            : journal.is_sales
            ? "Sale"
            : journal.tnx_type === "credit"
            ? "Credit"
            : "Debit",
          amount: parseFloat(journal.amount || 0),
          details: journal.note || journal.title || "No details",
          balance: parseFloat(journal.balance || 0),
          createdAt: journal.created_at || new Date().toISOString(),
          billAmount: parseFloat(journal.bill_amount || 0),
          paidAmount: parseFloat(journal.paid_amount || 0),
          isSales: journal.is_sales || false,
          salesId: salesId,
          returnId: journal.return_id || null,
          tnx_id: journal.tnx_id,
          tnx_date: journal.tnx_date,
          tnx_type: journal.tnx_type,
          note: journal.note || journal.title || "",
          bill_amount: journal.bill_amount || "0.00",
          paid_amount: journal.paid_amount || "0.00",
          amount_raw: journal.amount || "0.00",
        };
      });

      setTransactions(transformedTransactions);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      // Fallback to sample data for demonstration
      const sampleTransactions = [
        {
          id: "1",
          customerId: id,
          date: "2024-01-20",
          invoiceId: "TRN_001",
          type: "Credit",
          amount: 500.0,
          details: "Sample credit transaction",
          balance: 500.0,
          finalAmount: 500.0,
          createdAt: new Date().toISOString(),
        },
        {
          id: "2",
          customerId: id,
          date: "2024-01-21",
          invoiceId: "TRN_002",
          type: "Debit",
          amount: -200.0,
          details: "Sample debit transaction",
          balance: -200.0,
          finalAmount: 300.0,
          createdAt: new Date().toISOString(),
        },
      ];
      setTransactions(sampleTransactions);
      toast.warning("Using sample data - API connection failed");
    }
  };

  useEffect(() => {
    if (!id) return;

    // Prevent double execution in React StrictMode
    // Check both if we've fetched this ID and if we're currently fetching
    if (fetchedIdRef.current === id || isFetchingRef.current) {
      return;
    }

    // Mark as fetching and mark this ID as fetched
    isFetchingRef.current = true;
    fetchedIdRef.current = id;

    // Make API calls
    const fetchData = async () => {
      await Promise.all([
        fetchCustomerDetail(),
        fetchTransactions(),
        fetchCustomerSummaryStats(),
      ]);
      setIsInitialMount(false);
      isFetchingRef.current = false;
    };

    fetchData();
  }, [id]);

  // Handle search with debouncing
  useEffect(() => {
    // Skip on initial mount to avoid duplicate API call
    if (isInitialMount || !id) return;

    const delayedSearch = setTimeout(() => {
      fetchTransactions(searchTerm);
    }, 500); // Debounce search by 500ms

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, id]);

  // Handle filter changes
  useEffect(() => {
    // Skip on initial mount to avoid duplicate API call
    if (isInitialMount || !id) return;

    fetchTransactions(searchTerm);
  }, [selectedStartDate, selectedEndDate, typeFilter, balanceFilter, id]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        exportDropdownRef.current &&
        !exportDropdownRef.current.contains(event.target)
      ) {
        setShowExportDropdown(false);
      }
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setShowFilterDropdown(false);
      }
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const formatBalanceForSummary = (balance) => {
    if (balance === null || balance === undefined || balance === 0)
      return "0.00";
    const amount = parseFloat(balance);
    if (isNaN(amount)) return "0.00";
    return amount.toFixed(2);
  };

  const handleAddTransaction = async (transactionData) => {
    try {
      // Determine transaction type and amount
      let amount = 0;
      let tnx_type = "credit";

      if (transactionData.credit && parseFloat(transactionData.credit) > 0) {
        amount = parseFloat(transactionData.credit);
        tnx_type = "credit";
      } else if (
        transactionData.debit &&
        parseFloat(transactionData.debit) > 0
      ) {
        amount = parseFloat(transactionData.debit);
        tnx_type = "debit";
      } else if (transactionData.amount) {
        amount = parseFloat(transactionData.amount);
        tnx_type = transactionData.tnx_type || "credit";
      }

      const journalData = {
        title: transactionData.title || "Customer Transaction",
        tnx_id: `TRN_${Date.now().toString().slice(-6)}`,
        tnx_date:
          transactionData.date || new Date().toISOString().split("T")[0],
        customer: parseInt(transactionData.selectedCustomerId || id),
        customer_name: customer?.name || "",
        amount: amount,
        tnx_type: tnx_type,
        note: transactionData.note || transactionData.details || "",
      };

      const { financeJournalAPI } = await import("../../services");
      await financeJournalAPI.createJournal(journalData);
      toast.success("Transaction added successfully!");
      setShowFinanceForm(false);

      // Add a small delay to ensure backend has processed the changes
      setTimeout(() => {
        fetchTransactions(); // Refresh transactions
        fetchCustomerSummaryStats(); // Refresh summary statistics
      }, 500);
    } catch (error) {
      console.error("Error adding transaction:", error);
      console.error("Error response:", error.response?.data);
      toast.error(
        "Failed to add transaction: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const handleEditFinanceTransaction = async (transactionData) => {
    try {
      // Support both EditFinanceTransactionForm shape and API shape
      const providedType = transactionData.tnx_type || transactionData.type;
      const normalizedType = providedType
        ? String(providedType).toLowerCase()
        : editingTransaction.type === "Credit"
        ? "credit"
        : "debit";

      const journalData = {
        title: transactionData.title || editingTransaction.details,
        tnx_id:
          transactionData.tnx_id ||
          transactionData.invoiceId ||
          editingTransaction.invoiceId,
        tnx_date:
          transactionData.tnx_date ||
          transactionData.date ||
          editingTransaction.date,
        customer: parseInt(transactionData.selectedCustomerId || id),
        // Do not send customer_name on edit per request
        amount: parseFloat(
          transactionData.amount !== undefined &&
            transactionData.amount !== null &&
            transactionData.amount !== ""
            ? String(transactionData.amount).replace(/[�,�,]/g, "")
            : editingTransaction.amount
        ),
        tnx_type: normalizedType,
        note:
          transactionData.note ||
          transactionData.details ||
          editingTransaction.details,
      };

      // Note: For updating transactions, we still need to use finance journal API
      // as this is a write operation that should go through the finance system
      const { financeJournalAPI } = await import("../../services");
      await financeJournalAPI.updateJournal(editingTransaction.id, journalData);
      toast.success("Transaction updated successfully!");
      setShowEditFinanceForm(false);
      setEditingTransaction(null);

      // Add a small delay to ensure backend has processed the changes
      setTimeout(() => {
        fetchTransactions(); // Refresh transactions
        fetchCustomerSummaryStats(); // Refresh summary statistics
      }, 500);
    } catch (error) {
      toast.error("Failed to update transaction");
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    try {
      // Note: For deleting transactions, we still need to use finance journal API
      // as this is a write operation that should go through the finance system
      const { financeJournalAPI } = await import("../../services");
      await financeJournalAPI.deleteJournal(transactionId);
      toast.success("Transaction deleted successfully!");
      setShowDeleteModal(false);
      setDeletingTransaction(null);

      // Add a small delay to ensure backend has processed the changes
      setTimeout(() => {
        fetchTransactions(); // Refresh transactions
        fetchCustomerSummaryStats(); // Refresh summary statistics
      }, 500);
    } catch (error) {
      console.error("Error deleting transaction:", error);
      toast.error("Failed to delete transaction");
    }
  };

  // Calculate statistics
  const totalTransactions = transactions.length;

  // Use customer's stored balance instead of calculating from transactions
  const currentBalance = customer ? customer.balance || 0 : 0;

  const returns = 0; // Placeholder for returns count

  // Use transactions directly since filtering is now done server-side
  const filteredTransactions = transactions;

  // Pagination for transactions
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTransactions = filteredTransactions.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <div
          className="spinner-border"
          role="status"
          style={{ color: themePrimaryVar }}
        >
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="card shadow-sm rounded-3">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "400px" }}
        >
          <div className="text-center">
            <AlertCircle size={48} className="text-muted mb-3" />
            <h4 className="text-muted">Customer not found</h4>
            <p className="text-muted">
              The customer you're looking for doesn't exist.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const exportOptions = [
    { value: "pdf", label: "PDF", icon: FilePdf },
    { value: "excel", label: "Excel", icon: FileExcel },
    { value: "csv", label: "CSV", icon: FileCsv },
  ];

  const balanceFilterOptions = [
    { value: "all", label: "All" },
    { value: "positive", label: "Credit" },
    { value: "negative", label: "Debit" },
  ];

  const typeFilterOptions = [
    { value: "all", label: "All" },
    { value: "credit", label: "Credit" },
    { value: "debit", label: "Debit" },
    { value: "sale", label: "Sale" },
    { value: "return", label: "Return" },
  ];

  // Helper function to safely get field value
  const getFieldValue = (value, defaultValue = "") => {
    if (value === null || value === undefined || value === "") {
      return defaultValue;
    }
    return String(value);
  };

  // Fetch all transactions for export (without pagination limit)
  const fetchAllTransactionsForExport = async () => {
    try {
      // Helper to format date as YYYY-MM-DD in local timezone
      const formatLocalDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      // Prepare filters object with current state values
      const filterParams = {
        start_date: selectedStartDate
          ? formatLocalDate(selectedStartDate)
          : null,
        end_date: selectedEndDate ? formatLocalDate(selectedEndDate) : null,
        type:
          typeFilter === "credit" || typeFilter === "debit"
            ? typeFilter
            : null,
        avoid_zero_balance: "1",
      };

      // Remove null/undefined values
      const cleanFilters = Object.fromEntries(
        Object.entries(filterParams).filter(
          ([_, value]) => value !== null && value !== undefined
        )
      );

      // Pass allData=true to get all records without pagination
      const result = await customersAPI.getCustomerFinanceJournals(
        id,
        searchTerm,
        cleanFilters,
        true // allData parameter
      );

      if (result.error) {
        toast.error(result.error.message || "Failed to fetch data for export");
        return [];
      }

      // Handle different response structures
      let journalData = [];
      if (result.response?.data) {
        journalData = Array.isArray(result.response.data)
          ? result.response.data
          : [];
      } else if (Array.isArray(result.response)) {
        journalData = result.response;
      }

      if (journalData.length === 0) {
        return [];
      }

      // Transform journal data to transaction format
      const transformedTransactions = journalData.map((journal) => {
        // Extract sales ID from note if sales field is null
        let salesId = journal.sales;
        if (!salesId && journal.note) {
          const match = journal.note.match(/Sales id : (\d+)/);
          if (match) salesId = parseInt(match[1]);
        }

        return {
          id: journal.id,
          customerId: id,
          date: journal.tnx_date,
          invoiceId: journal.tnx_id,
          type: journal.return_id
            ? "Return"
            : journal.is_sales
            ? "Sale"
            : journal.tnx_type === "credit"
            ? "Credit"
            : "Debit",
          amount: parseFloat(journal.amount || 0),
          details: journal.note || journal.title || "No details",
          balance: parseFloat(journal.balance || 0),
          createdAt: journal.created_at || new Date().toISOString(),
          billAmount: parseFloat(journal.bill_amount || 0),
          paidAmount: parseFloat(journal.paid_amount || 0),
          isSales: journal.is_sales || false,
          salesId: salesId,
          returnId: journal.return_id || null,
          tnx_id: journal.tnx_id,
          tnx_date: journal.tnx_date,
          tnx_type: journal.tnx_type,
          note: journal.note || journal.title || "",
          bill_amount: journal.bill_amount || "0.00",
          paid_amount: journal.paid_amount || "0.00",
          amount_raw: journal.amount || "0.00",
        };
      });

      return transformedTransactions;
    } catch (error) {
      console.error("Error fetching all transactions for export:", error);
      toast.error("Failed to fetch data for export");
      return [];
    }
  };

  const exportToExcel = async (allTransactions = null) => {
    if (filteredTransactions.length === 0) {
      toast.warning("No data to export");
      return;
    }

    const exportTransactions =
      allTransactions || (await fetchAllTransactionsForExport());

    if (exportTransactions.length === 0) {
      toast.error("No data to export");
      return;
    }

    // Create summary header rows
    const customerName = customer?.name || "Customer";
    const currentBalanceValue = summaryStats.current_balance || currentBalance || 0;
    const summaryData = [
      { A: `${customerName} - Finance History` },
      { A: `Current Balance: €${formatBalanceForSummary(currentBalanceValue)} | Total Orders: ${summaryStats.total_orders || totalTransactions} | Returns: ${summaryStats.returns || returns}` },
      { A: `Generated on: ${new Date().toLocaleDateString()}` },
      {}, // Empty row
    ];

    // Prepare data for Excel with user-friendly headers
    const excelData = exportTransactions.map((transaction) => {
      const type = transaction.type || "Credit";
      const amountSign = type === "Credit" || type === "Sale" ? "-" : "+";
      const formattedAmount = `${amountSign}€${Math.abs(
        transaction.amount || 0
      ).toFixed(2)}`;
      const formattedBalance = `€${Math.abs(transaction.balance || 0).toFixed(
        2
      )}`;

      let details = "";
      if (type === "Sale") {
        details = `Bill: €${
          transaction.billAmount?.toFixed(2) || "0.00"
        }, Paid: €${transaction.paidAmount?.toFixed(2) || "0.00"}`;
      } else if (transaction.returnId) {
        details = `Return: €${transaction.bill_amount || "0.00"}, Exchange: €${
          transaction.paid_amount || "0.00"
        }`;
      } else {
        details = transaction.note || transaction.details || "";
      }

      return {
        DATE: getFieldValue(transaction.date),
        "INV/RTN/TRN ID": getFieldValue(transaction.invoiceId),
        TYPE: type,
        DETAILS: details,
        "CREDIT / DEBIT": formattedAmount,
        BALANCE: formattedBalance,
      };
    });

    // Create worksheet with summary first
    const ws = XLSX.utils.json_to_sheet(summaryData, { skipHeader: true });
    XLSX.utils.sheet_add_json(ws, excelData, { origin: -1 });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Finance History");

    // Set column widths
    ws["!cols"] = [
      { wch: 15 }, // DATE
      { wch: 20 }, // INV/RTN/TRN ID
      { wch: 12 }, // TYPE
      { wch: 45 }, // DETAILS
      { wch: 18 }, // CREDIT / DEBIT
      { wch: 15 }, // BALANCE
    ];

    // Save the Excel file
    const timestamp = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `${customerName}_Finance_History_${timestamp}.xlsx`);
    toast.success("Excel file exported successfully");
  };

  const exportToCSV = async (allTransactions = null) => {
    if (filteredTransactions.length === 0) {
      toast.warning("No data to export");
      return;
    }

    const exportTransactions =
      allTransactions || (await fetchAllTransactionsForExport());

    if (exportTransactions.length === 0) {
      toast.error("No data to export");
      return;
    }

    // Create summary header rows
    const customerName = customer?.name || "Customer";
    const currentBalanceValue = summaryStats.current_balance || currentBalance || 0;
    const summaryRows = [
      [`${customerName} - Finance History`],
      [`Current Balance: €${formatBalanceForSummary(currentBalanceValue)} | Total Orders: ${summaryStats.total_orders || totalTransactions} | Returns: ${summaryStats.returns || returns}`],
      [`Generated on: ${new Date().toLocaleDateString()}`],
      [], // Empty row
    ];

    // Prepare CSV data with user-friendly headers
    const csvHeaders = [
      "DATE",
      "INV/RTN/TRN ID",
      "TYPE",
      "DETAILS",
      "CREDIT / DEBIT",
      "BALANCE",
    ];
    const csvData = exportTransactions.map((transaction) => {
      const type = transaction.type || "Credit";
      const amountSign = type === "Credit" || type === "Sale" ? "-" : "+";
      const formattedAmount = `${amountSign}€${Math.abs(
        transaction.amount || 0
      ).toFixed(2)}`;
      const formattedBalance = `€${Math.abs(transaction.balance || 0).toFixed(
        2
      )}`;

      let details = "";
      if (type === "Sale") {
        details = `Bill: €${
          transaction.billAmount?.toFixed(2) || "0.00"
        }, Paid: €${transaction.paidAmount?.toFixed(2) || "0.00"}`;
      } else if (transaction.returnId) {
        details = `Return: €${transaction.bill_amount || "0.00"}, Exchange: €${
          transaction.paid_amount || "0.00"
        }`;
      } else {
        details = transaction.note || transaction.details || "";
      }

      return [
        getFieldValue(transaction.date),
        getFieldValue(transaction.invoiceId),
        type,
        details,
        formattedAmount,
        formattedBalance,
      ];
    });

    // Combine summary, headers and data
    const csvContent = [...summaryRows, csvHeaders, ...csvData]
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const timestamp = new Date().toISOString().split("T")[0];
    link.setAttribute(
      "download",
      `${customerName}_Finance_History_${timestamp}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV file exported successfully");
  };

  const handleExportSelect = async (value) => {
    setShowExportDropdown(false);

    if (filteredTransactions.length === 0) {
      toast.warning("No data to export");
      return;
    }

    toast.info(`Preparing ${value.toUpperCase()} export...`);
    const exportTransactions = await fetchAllTransactionsForExport();

    if (exportTransactions.length === 0) {
      toast.error("No data to export");
      return;
    }

    switch (value) {
      case "pdf":
        // Navigate to CustomerDetailsPrint page with customer and all transaction data
        navigate(`/customer/${id}/print`, {
          state: {
            customer: customer,
            transactions: exportTransactions,
            currentBalance: summaryStats.current_balance || currentBalance || 0,
            summaryStats: summaryStats,
          },
        });
        break;
      case "excel":
        await exportToExcel(exportTransactions);
        break;
      case "csv":
        await exportToCSV(exportTransactions);
        break;
      default:
        toast.warning(`Export format ${value} not supported`);
    }
  };

  const handleBalanceFilterSelect = (value) => {
    setBalanceFilter(value);
    setShowFilterDropdown(false);
  };

  const handleTypeFilterSelect = (value) => {
    setTypeFilter(value);
    setShowFilterDropdown(false);
  };

  // Date picker functions
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    return date > today;
  };

  const isDateInRange = (date) => {
    if (!selectedStartDate || !selectedEndDate) return false;
    return date >= selectedStartDate && date <= selectedEndDate;
  };

  const isDateSelected = (date) => {
    if (!selectedStartDate && !selectedEndDate) return false;
    return (
      (selectedStartDate && date.getTime() === selectedStartDate.getTime()) ||
      (selectedEndDate && date.getTime() === selectedEndDate.getTime())
    );
  };

  const handleDateClick = (date) => {
    if (isDateDisabled(date)) return;

    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      setSelectedStartDate(date);
      setSelectedEndDate(null);
    } else if (selectedStartDate && !selectedEndDate) {
      if (date < selectedStartDate) {
        setSelectedEndDate(selectedStartDate);
        setSelectedStartDate(date);
      } else {
        setSelectedEndDate(date);
      }
    }
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    // Adjust for Monday as first day: 0=Sunday becomes 6, 1=Monday becomes 0, etc.
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const formatDateRange = () => {
    if (selectedStartDate && selectedEndDate) {
      const startStr = selectedStartDate.toLocaleDateString("en-GB");
      const endStr = selectedEndDate.toLocaleDateString("en-GB");
      return `${startStr} - ${endStr}`;
    } else if (selectedStartDate) {
      const startStr = selectedStartDate.toLocaleDateString("en-GB");
      return `${startStr} - `;
    }
    return "Select date range";
  };

  return (
    <>
      {/* Back Navigation - Outside the card */}
      <div className="customer-details-back">
        <button
          className={`${themeClass("customer-details-back-btn")}`}
          onClick={() => navigate(returnToUrl)}
        >
          <ArrowLeft size={17} className="me-2" />
          Back to Customers
        </button>
      </div>

      {/* Main Content Card */}
      <div className="customer-details-container">
        {/* Header */}
        <div className="customer-details-header">
          <div className="d-flex align-items-center">
            <h1 className="customer-details-title">{customer.name}</h1>
            <span
              className={`${themeClass("customer-details-status")} ${customer.status === "Active" ? "active" : "inactive"}`}
            >
              {customer.status || "Inactive"}
            </span>
          </div>
          <button
            className={`${themeClass("add-customer-btn")}`}
            onClick={() => setShowFinanceForm(true)}
          >
            <Plus size={16} className="me-1" />
            Add Transaction
          </button>
        </div>

        {/* Contact Information */}
        <div className="customer-details-contact">
          <div className="customer-details-contact-item">
            <Mail size={16} />
            <span>{customer.user_email || customer.email || "N/A"}</span>
          </div>
          <div className="customer-details-contact-item">
            <Phone size={16} />
            <span>{customer.mobile || customer.phone || "N/A"}</span>
          </div>
          <div className="customer-details-contact-item">
            <MapPin size={16} />
            <span>{customer.address || "N/A"}</span>
          </div>
        </div>

        {/* Tabs */}
        <div
          className={`${themeClass("customer-details-tabs")}`}
        >
          <button
            className={`${themeClass("customer-details-tab")} ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>
          <button
            className={`${themeClass("customer-details-tab")} ${activeTab === "analytics" ? "active" : ""}`}
            onClick={() => setActiveTab("analytics")}
          >
            Analytics
          </button>
          <button
            className={`${themeClass("customer-details-tab")} ${activeTab === "returns" ? "active" : ""}`}
            onClick={() => setActiveTab("returns")}
          >
            Returns
          </button>
        </div>

        {activeTab === "overview" && (
          <div className="customer-details-overview">
            {/* Summary Statistics */}
            <div className="customer-details-summary">
              <div
                className={`customer-details-summary-card ${
                  (summaryStats.current_balance || currentBalance) >= 0
                    ? "positive"
                    : "negative"
                }`}
              >
                <p className="customer-details-summary-label">
                  Current Balance
                </p>
                <h4
                  className={`customer-details-summary-value ${
                    (summaryStats.current_balance || currentBalance) >= 0
                      ? "positive"
                      : "negative"
                  }`}
                >
                  {summaryLoading ? (
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    <MaskedCurrencyValue
                      value={`€${formatBalanceForSummary(
                        summaryStats.current_balance || currentBalance || 0
                      )}`}
                    />
                  )}
                </h4>
              </div>

              <div className="customer-details-summary-card">
                <p className="customer-details-summary-label">Total Order</p>
                <h4 className="customer-details-summary-value">
                  {summaryLoading ? (
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    summaryStats.total_orders || totalTransactions
                  )}
                </h4>
              </div>

              <div className="customer-details-summary-card">
                <p className="customer-details-summary-label">Returns</p>
                <h4 className="customer-details-summary-value">
                  {summaryLoading ? (
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    summaryStats.returns || returns
                  )}
                </h4>
              </div>
            </div>

            {/* Finance History Card */}
            <div className="customer-details-finance">
              <div className="customer-details-finance-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="customer-details-finance-title">
                    Finance History
                  </h5>

                  {/* Search and Filter Bar */}
                  <div className="customer-details-search-filter">
                    {/* Search Input */}
                    <div className="customer-details-search">
                      <Search
                        size={16}
                        className="customer-details-search-icon"
                      />
                      <input
                        type="text"
                        className={`${themeClass("customer-details-search-input")}`}
                        placeholder="Search by ID or amount..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>

                    {/* Date Range Input */}
                    <div
                      className="customer-details-date-range"
                      ref={datePickerRef}
                    >
                      <Calendar
                        size={16}
                        className="customer-details-date-icon"
                      />
                      <input
                        type="text"
                        className={`${themeClass("customer-details-date-input")}`}
                        value={formatDateRange()}
                        readOnly
                        onClick={() => setShowDatePicker(!showDatePicker)}
                      />

                      {/* Date Picker Popup */}
                      {showDatePicker && (
                        <>
                          <div
                            className="date-picker-backdrop"
                            onClick={() => setShowDatePicker(false)}
                          ></div>
                          <div className="customer-detail-date-picker">
                            <div className="date-picker-header">
                              <button
                                className="date-picker-nav-btn"
                                onClick={() => navigateMonth(-1)}
                              >
                                <ChevronDown
                                  size={16}
                                  style={{ transform: "rotate(90deg)" }}
                                />
                              </button>
                              <h3 className="date-picker-month-year">
                                {currentMonth.toLocaleDateString("en-US", {
                                  month: "long",
                                  year: "numeric",
                                })}
                              </h3>
                              <button
                                className="date-picker-nav-btn"
                                onClick={() => navigateMonth(1)}
                              >
                                <ChevronDown
                                  size={16}
                                  style={{ transform: "rotate(-90deg)" }}
                                />
                              </button>
                            </div>

                            <div className="date-picker-weekdays">
                              <div className="weekday">Mo</div>
                              <div className="weekday">Tu</div>
                              <div className="weekday">We</div>
                              <div className="weekday">Th</div>
                              <div className="weekday">Fr</div>
                              <div className="weekday">Sa</div>
                              <div className="weekday">Su</div>
                            </div>

                            <div className="date-picker-days">
                              {getDaysInMonth(currentMonth).map(
                                (day, index) => (
                                  <div
                                    key={index}
                                    className={`${themeClass("date-picker-day")} ${day ? "" : "empty"} ${
                                      day && isDateDisabled(day)
                                        ? "disabled"
                                        : ""
                                    } ${
                                      day && isDateInRange(day)
                                        ? "in-range"
                                        : ""
                                    } ${
                                      day && isDateSelected(day)
                                        ? "selected"
                                        : ""
                                    }`}
                                    onClick={() => day && handleDateClick(day)}
                                  >
                                    {day ? day.getDate() : ""}
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Filter Button */}
                    <div
                      className="customer-detail-filter-dropdown"
                      ref={filterDropdownRef}
                    >
                      <button
                        className={`${themeClass("customer-detail-filter-button")}`}
                        onClick={() =>
                          setShowFilterDropdown(!showFilterDropdown)
                        }
                      >
                        <Filter size={16} />
                      </button>

                      <div
                        className={`customer-detail-filter-dropdown-content no-scrollbar ${
                          showFilterDropdown ? "show" : ""
                        }`}
                      >
                        {/* Balance Filter Section */}
                        <div className="filter-section">
                          <div className="filter-section-header">
                            <span>Balance</span>
                            <ChevronDown size={12} />
                          </div>
                          <div className="filter-options">
                            {balanceFilterOptions.map((option) => (
                              <div
                                key={option.value}
                                className={`${themeClass("filter-option")}`}
                                onClick={() =>
                                  handleBalanceFilterSelect(option.value)
                                }
                              >
                                <input
                                  type="radio"
                                  name="balance"
                                  value={option.value}
                                  checked={balanceFilter === option.value}
                                  onChange={() =>
                                    handleBalanceFilterSelect(option.value)
                                  }
                                />
                                <label>{option.label}</label>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Type Filter Section */}
                        <div className="filter-section">
                          <div className="filter-section-header">
                            <span>Type</span>
                            <ChevronDown size={12} />
                          </div>
                          <div className="filter-options">
                            {typeFilterOptions.map((option) => (
                              <div
                                key={option.value}
                                className={`${themeClass("filter-option")}`}
                                onClick={() =>
                                  handleTypeFilterSelect(option.value)
                                }
                              >
                                <input
                                  type="radio"
                                  name="type"
                                  value={option.value}
                                  checked={typeFilter === option.value}
                                  onChange={() =>
                                    handleTypeFilterSelect(option.value)
                                  }
                                />
                                <label>{option.label}</label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Export Dropdown */}
                    <div
                      className="customer-detail-filter-dropdown"
                      ref={exportDropdownRef}
                    >
                      <button
                        className={`${themeClass("customer-detail-filter-button")}`}
                        onClick={() =>
                          setShowExportDropdown(!showExportDropdown)
                        }
                      >
                        <Download size={16} />
                        Export
                        <ChevronDown
                          size={12}
                          className={`customer-detail-export-dropdown-arrow ${
                            showExportDropdown ? "rotated" : ""
                          }`}
                        />
                      </button>

                      <div
                        className={`customer-detail-export-dropdown-content ${
                          showExportDropdown ? "show" : ""
                        }`}
                      >
                        {exportOptions.map((option) => (
                          <div
                            key={option.value}
                            className="customer-detail-export-dropdown-item"
                            onClick={() => handleExportSelect(option.value)}
                          >
                            <img
                              src={option.icon}
                              alt={option.label}
                              width="16"
                              height="16"
                            />
                            {option.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Finance History Table */}
              <div className="p-3">
                {/* Finance History Table */}
                {currentTransactions.length === 0 ? (
                  <div className="customer-details-empty">
                    <h6>
                      {searchTerm ||
                      balanceFilter !== "all" ||
                      typeFilter !== "all" ||
                      selectedStartDate ||
                      selectedEndDate
                        ? "No transactions match your filters"
                        : "No finance history yet"}
                    </h6>
                    <p>
                      {searchTerm ||
                      balanceFilter !== "all" ||
                      typeFilter !== "all" ||
                      selectedStartDate ||
                      selectedEndDate
                        ? "Try adjusting search or filters to see results."
                        : "Finance history will appear here once you add transactions for this customer."}
                    </p>
                  </div>
                ) : (
                  <div className="customer-table-responsive">
                    <table className="journal-table">
                      <thead>
                        <tr>
                          <th>DATE</th>
                          <th>INV/RTN/TRN ID</th>
                          <th style={{ width: "100px" }}>TYPE</th>
                          <th style={{ width: "220px" }}>DETAILS</th>
                          <th>CREDIT / DEBIT</th>
                          <th>BALANCE</th>
                          <th style={{ textAlign: "center" }}>ACTION</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentTransactions.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="no-data">
                              No transactions found
                            </td>
                          </tr>
                        ) : (
                          currentTransactions.map((transaction) => (
                            <tr key={transaction.id}>
                              <td>{transaction.date}</td>
                              <td>{transaction.invoiceId}</td>
                              <td style={{ width: "100px" }}>
                                <div className="customer-details-transaction-type">
                                  {transaction.type === "Sale" && (
                                    <>
                                      <SaleIcon />

                                      <span>Sale</span>
                                    </>
                                  )}
                                  {transaction.type === "Return" && (
                                    <>
                                      <ReturnIcon />

                                      <span>Return</span>
                                    </>
                                  )}
                                  {transaction.type === "Credit" && (
                                    <>
                                      <CreditIcon />

                                      <span className="customer-details-type-credit">Credit</span>
                                    </>
                                  )}
                                  {transaction.type === "Debit" && (
                                    <>
                                      <DebitIcon />

                                      <span className="customer-details-type-debit">Debit</span>
                                    </>
                                  )}
                                </div>
                              </td>
                              <td style={{ width: "220px" }}>
                                {transaction.type === "Sale" ? (
                                  <div className="d-flex gap-4 flex-row">
                                    <div className="d-flex flex-column">
                                      <p className="text-muted">Bill Amount</p>
                                      <div className="customer-details-amount">
                                        €
                                        {transaction.billAmount?.toFixed(2) ||
                                          "0.00"}
                                      </div>
                                    </div>

                                    <div className="d-flex flex-column">
                                      <p className="text-muted">Paid</p>
                                      <div className="customer-details-amount positive">
                                        €
                                        {transaction.paidAmount?.toFixed(2) ||
                                          "0.00"}
                                      </div>
                                    </div>
                                  </div>
                                ) : transaction.returnId ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "4px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                      }}
                                    >
                                      <span
                                        style={{
                                          color: "#57606A",
                                          fontSize: "14px",
                                        }}
                                      >
                                        Return Device
                                      </span>
                                      <span
                                        style={{
                                          color: "#57606A",
                                          fontSize: "14px",
                                          fontWeight: "500",
                                        }}
                                      >
                                        €{transaction.bill_amount}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                      }}
                                    >
                                      <span
                                        style={{
                                          color: "#57606A",
                                          fontSize: "14px",
                                        }}
                                      >
                                        Exchanged Device
                                      </span>
                                      <span
                                        style={{
                                          color: "#D800B0",
                                          fontSize: "14px",
                                          fontWeight: "500",
                                        }}
                                      >
                                        €{transaction.paid_amount}
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <span
                                    title={transaction.details}
                                    style={{
                                      display: "inline-block",
                                      maxWidth: "190px",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {transaction.details}
                                  </span>
                                )}
                              </td>
                              {/* Amount */}
                              <td>
                                {transaction.tnx_type === "debit" ? (
                                  <div className="customer-details-amount positive">
                                    + €
                                    {Math.abs(transaction.amount || 0).toFixed(
                                      2
                                    )}
                                  </div>
                                ) : (
                                  <div className="customer-details-amount negative">
                                    - €
                                    {Math.abs(transaction.amount || 0).toFixed(
                                      2
                                    )}
                                  </div>
                                )}
                              </td>
                              {/*  Balance Amount */}
                              <td>
                                <div
                                  className={`balance-badge ${
                                    transaction.balance < 0
                                      ? "negative"
                                      : "positive"
                                  }`}
                                >
                                  {transaction.balance < 0 ? "-" : ""}€
                                  {Math.abs(transaction.balance || 0).toFixed(
                                    2
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="action-buttons">
                                  {/* Navigation to sales detail page */}
                                  <button
                                    className={`${themeClass("action-btn")} view ${
                                      transaction.type === "Debit" ||
                                      transaction.type === "Credit"
                                        ? "disabled"
                                        : ""
                                    }`}
                                    onClick={() => {
                                      if (transaction.salesId) {
                                        navigate(
                                          `/sales/view/${transaction.salesId}`
                                        );
                                      }
                                    }}
                                    disabled={!transaction.salesId}
                                    title={
                                      transaction.salesId
                                        ? "View sale details"
                                        : "No sale linked"
                                    }
                                  >
                                    <EyeIcon
                                      disabled={
                                        transaction.type === "Debit" ||
                                        transaction.type === "Credit"
                                      }
                                      fill={themePrimaryVar}
                                    />
                                  </button>

                                  <button
                                    className={`${themeClass("action-btn")} view ${
                                      transaction.isSales ||
                                      transaction.returnId
                                        ? "disabled"
                                        : ""
                                    }`}
                                    onClick={() => {
                                      if (
                                        !transaction.isSales &&
                                        !transaction.returnId
                                      ) {
                                        setEditingTransaction(transaction);
                                        setShowEditFinanceForm(true);
                                      }
                                    }}
                                    disabled={
                                      transaction.isSales ||
                                      transaction.returnId
                                    }
                                    title={
                                      transaction.isSales
                                        ? "Edit not available for sales"
                                        : transaction.returnId
                                        ? "Edit not available for returns"
                                        : "Edit transaction"
                                    }
                                  >
                                    <EditIcon
                                      fill={
                                        themePrimaryVar
                                      }
                                      disabled={
                                        transaction.isSales ||
                                        transaction.returnId
                                      }
                                    />
                                  </button>

                                  <button
                                    className={`action-btn delete ${
                                      transaction.isSales ||
                                      transaction.returnId
                                        ? "disabled"
                                        : ""
                                    }`}
                                    onClick={() => {
                                      if (
                                        !transaction.isSales &&
                                        !transaction.returnId
                                      ) {
                                        setDeletingTransaction(transaction);
                                        setShowDeleteModal(true);
                                      }
                                    }}
                                    disabled={
                                      transaction.isSales ||
                                      transaction.returnId
                                    }
                                    title={
                                      transaction.isSales
                                        ? "Delete not available for sales"
                                        : transaction.returnId
                                        ? "Delete not available for returns"
                                        : "Delete"
                                    }
                                  >
                                    <RedDeleteIcon
                                      disabled={
                                        transaction.isSales ||
                                        transaction.returnId
                                      }
                                    />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {filteredTransactions.length > itemsPerPage && (
                  <div className="customer-details-pagination">
                    <div className="customer-details-pagination-info">
                      Page {currentPage} of {totalPages}
                    </div>
                    <div className="customer-details-pagination-buttons">
                      <button
                        className={`${themeClass("customer-details-pagination-btn")}`}
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <button
                        className={`${themeClass("customer-details-pagination-btn")}`}
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "analytics" && (
          <CustomerAnalytics
            customerId={id}
            accentColor={themePrimaryVar}
            themeClass={themeClass}
          />
        )}

        {activeTab === "returns" && (
          <div className="customer-details-returns">
            <div className="text-center py-5">
              <h5 className="text-muted">Returns</h5>
              <p className="text-muted">
                Returns functionality will be available soon.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Add Finance Transaction Modal */}
      {showFinanceForm && (
        <FinanceTransactionForm
          transaction={null}
          customerId={id}
          customerName={customer?.name}
          onSave={handleAddTransaction}
          onCancel={() => {
            setShowFinanceForm(false);
          }}
        />
      )}

      {/* Edit Finance Transaction Modal */}
      {showEditFinanceForm && editingTransaction && (
        <EditFinanceTransactionForm
          transaction={editingTransaction}
          customerId={id}
          onSave={handleEditFinanceTransaction}
          onCancel={() => {
            setShowEditFinanceForm(false);
            setEditingTransaction(null);
          }}
        />
      )}

      {/* Delete Modal */}
      {showDeleteModal && deletingTransaction && (
        <>
          <div className="modal-overlay">
            <div className="delete-modal">
              <div className="modal-icon">
                <img src={DeleteIcon} alt="Delete" />
              </div>
              <h3 className="modal-title">Delete Transaction</h3>
              <p className="modal-message">
                Are you sure you want to delete transaction{" "}
                <b>{deletingTransaction.invoiceId}</b>?
                <br />
                This action cannot be undone.
              </p>
              <div className="modal-actions">
                <button
                  className="modal-cancel-btn"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="modal-delete-btn"
                  onClick={() =>
                    handleDeleteTransaction(deletingTransaction.id)
                  }
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CustomerDetails;
